import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Divider } from 'antd';

export const CustomDivider = styled(Divider)`
  border-block-start-color: #000000;
  width: auto;
  min-width: unset;

  &.sdg-separator {
    margin: 0 80px;

    @media (max-width: 768px) {
      margin: 0 48px;
    }

    @media (max-width: 480px) {
      margin: 0 24px;
    }
  }

  .puppeteer-pdf & {
    transform: scaleY(0.5);
    transform-origin: 0 0;

    &.sdg-separator {
      margin: 0 40px;

      &.page-break-before {
        page-break-before: always;
        margin-top: 40px;
      }

      &.no-break {
        page-break-inside: avoid;
      }
    }
  }
`;

export const Block = styled.div<{
  $bgColor?: string;
  $bgImage?: string;
  $color?: string;
}>`
  &.report-title-block {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 564px;
    padding-top: 96px;
    padding-bottom: 46px;
    margin: 0 80px;
    background-repeat: no-repeat;
    background-size: 640px;
    background-position: right -60px bottom -80px;

    @media (max-width: 768px) {
      height: 320px;
      padding-top: 48px;
      padding-bottom: 23px;
      margin: 0 64px;
      background: unset;
    }

    @media (max-width: 480px) {
      height: 256px;
      padding-bottom: 16px;
    }
  }

  &.report-description-block {
    display: flex;
    height: 576px;

    @media (max-width: 768px) {
      flex-direction: column;
      height: 1152px;
    }

    @media (max-width: 480px) {
      height: 576px;
    }
  }

  &.about-text-block {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    row-gap: 24px;
    color: ${({ $color }) => $color || '#ffffff'};
    background-color: ${({ $bgColor }) => $bgColor};
    padding: 32px 144px 32px 168px;

    @media (max-width: 768px) {
      padding: 16px 84px;
    }

    @media (max-width: 480px) {
      padding: 16px 48px;
    }
  }

  &.about-image-block {
    width: 768px; /* 576px × (4/3) = 768px for 4:3 aspect ratio */
    flex-shrink: 0;
    background-size: cover;

    @media (max-width: 768px) {
      width: 100%;
      flex-grow: 1;
    }

    @media (max-width: 480px) {
    }
  }

  &.score-title-block {
    margin: 0 80px;

    @media (max-width: 768px) {
      margin: 0 64px;
    }

    @media (max-width: 480px) {
    }
  }

  &.sdg-item {
    display: flex;
    flex-direction: column;
    z-index: 1;

    &:first-of-type {
      margin-top: -32px;

      @media (max-width: 768px) {
        margin-top: 32px;
      }
    }
  }

  &.sdg-content {
    position: relative;
    display: flex;
    padding: 64px 160px;

    @media (max-width: 768px) {
      padding: 32px 80px;
    }

    @media (max-width: 480px) {
      padding: 16px 40px;
    }

    &::before {
      content: '';
      width: 100%;
      height: 40%;
      background-image: url(${({ $bgImage }) => $bgImage});
      background-size: contain;
      background-repeat: no-repeat;
      background-position: bottom left 80px;
      position: absolute;
      bottom: 0;
      left: 0;
      z-index: 0;

      @media (max-width: 768px) {
        content: none;
      }
    }
  }

  &.sdg-title-block {
    display: flex;
    flex-direction: column;
    row-gap: 12px;
    padding-right: 96px;

    @media (max-width: 768px) {
      padding-right: 48px;
    }

    @media (max-width: 480px) {
      padding-right: 24px;
    }
  }

  &.report-logo {
    height: 576px;
    background-image: url(${({ $bgImage }) => $bgImage});
    background-size: 256px;
    background-repeat: no-repeat;
    background-position: center center;
  }

  &.charts-section {
    height: 100%;
    color: #000000;
  }

  &.key-indicators-block {
    height: 100%;
    min-height: 976px;
    padding: 48px 0 0 0;

    @media (max-width: 768px) {
      min-height: 600px;
      padding: 24px 0 0 0;
    }

    @media (max-width: 480px) {
      min-height: 500px;
      padding: 16px 0 0 0;
    }
  }

  &.key-indicators-title-block {
    padding: 0 80px;

    @media (max-width: 768px) {
      padding: 0 64px;
    }

    @media (max-width: 480px) {
    }
  }

  &.key-indicators-content-block {
    position: relative;
    width: 100%;
    height: 64.25vw;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: transparent;
    overflow: hidden;

    @media (max-width: 768px) {
      height: 100vh;
      flex-direction: column;
      gap: 32px;
      padding: 32px 0;
    }

    @media (max-width: 480px) {
      gap: 24px;
      padding: 24px 0;
    }
  }

  &.radar-chart-block {
    min-height: 976px;
    padding: 32px 0;

    .echarts-for-react {
      height: 100vh !important;
      width: 100vh !important;

      @media (max-width: 768px) {
        height: 80vw !important;
        width: 80vw !important;
        margin: 0 auto;
      }

      @media (max-width: 480px) {
        height: 90vw !important;
        width: 90vw !important;
      }
    }

    @media (max-width: 768px) {
      min-height: auto;
      padding: 16px 0;
    }

    @media (max-width: 480px) {
      padding: 8px 0;
    }
  }

  &.radar-chart-title-block {
    padding: 0 80px;

    @media (max-width: 768px) {
      padding: 0 64px;
    }

    @media (max-width: 480px) {
    }
  }

  &.radar-chart-content {
    padding: 80px;

    @media (max-width: 768px) {
      padding: 40px 64px;
    }

    @media (max-width: 480px) {
      padding: 24px 24px;
    }
  }

  &.sdg-list-block {
    width: 100%;
    padding: 24px 0 0 80px;

    @media (max-width: 768px) {
      padding: 0;
      margin-top: 24px;
    }

    @media (max-width: 480px) {
      margin-top: 16px;
    }
  }

  &.desc-title-block {
    margin-bottom: 24px;
  }

  &.main-key-indicators-block {
    width: 38%;
    display: flex;
    flex-direction: column;
    row-gap: 20px;
    container-type: inline-size;

    @media (max-width: 768px) {
      width: 70%;
      row-gap: 16px;
    }

    @media (max-width: 480px) {
      width: 75%;
      row-gap: 12px;
    }
  }

  &.rest-key-indicators-block {
    width: 60%;
    display: flex;
    flex-direction: column;
    row-gap: 24px;
    background-image: url(${({ $bgImage }) => $bgImage});
    background-repeat: no-repeat;

    &.left {
      background-size: 128px;
      background-position: top -32px right -20px;

      @media (max-width: 768px) {
        background-size: 48px;
        background-position: top 0px right 16px;
      }

      @media (max-width: 480px) {
        background-size: 32px;
        background-position: top 0px right 12px;
      }
    }

    &.top-right {
      background-size: 128px;
      background-position: top -32px right -20px;

      @media (max-width: 768px) {
        background-size: 48px;
        background-position: top 0px right 16px;
      }

      @media (max-width: 480px) {
        background-size: 32px;
        background-position: top 0px right 12px;
      }
    }

    &.bottom-right {
      width: 62%;
      background-size: 128px;
      background-position: top -24px right -20px;

      @media (max-width: 768px) {
        width: 65%;
        background-size: 48px;
        background-position: top 0px right 16px;
      }

      @media (max-width: 480px) {
        width: 70%;
        background-size: 32px;
        background-position: top 0px right 16px;
      }
    }

    @media (max-width: 768px) {
      width: 65%;
      row-gap: 16px;
    }

    @media (max-width: 480px) {
      width: 70%;
      row-gap: 12px;
    }
  }

  .puppeteer-pdf & {
    &.report-title-block {
      height: 282px;
      padding-top: 48px;
      padding-bottom: 23px;
      margin: 0 40px;
      background-size: 320px;
      background-position: right -30px bottom -37px;
    }

    &.report-description-block {
      height: 288px;
    }

    &.about-text-block {
      flex: 1;
      row-gap: 12px;
      padding: 16px 72px 16px 84px;
      display: -webkit-box;
      -webkit-line-clamp: 8;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    &.about-image-block {
      width: 384px; /* 288px × (4/3) = 384px for 4:3 aspect ratio */
      flex-shrink: 0;
      background-size: cover;
    }

    &.score-title-block {
      margin: 0 40px;
    }

    &.sdg-item {
      &:first-of-type {
        margin-top: -16px;
      }
    }

    &.sdg-content {
      padding: 32px 80px;

      &::before {
        background-position: bottom left 40px;
      }
    }

    &.sdg-title-block {
      row-gap: 6px;
      padding-right: 48px;
    }

    &.report-logo {
      height: 288px;
      background-size: 128px;
    }

    &.charts-section {
      height: 297mm;
    }

    &.key-indicators-block {
      height: 148.5mm;
      min-height: 148.5mm;
      max-height: 148.5mm;
      padding: 24px 0 0 0;
      overflow: hidden;

      .echarts-for-react {
        height: 592px !important;
      }
    }

    &.key-indicators-title-block {
      padding: 0 40px;
    }

    &.key-indicators-content-block {
      height: 100%;
    }

    &.radar-chart-block {
      height: 148.5mm;
      min-height: 148.5mm;
      max-height: 148.5mm;
      padding: 16px 0 0 0;
      font-size: 0.25rem;

      .echarts-for-react {
        height: 408px !important;
        width: 408px !important;
      }
    }

    &.radar-chart-title-block {
      padding: 0 40px;
    }

    &.radar-chart-content {
      padding: 40px;
    }

    &.sdg-list-block {
      padding: 12px 0 0 40px;
    }

    &.desc-title-block {
      margin-bottom: 12px;
    }

    &.main-key-indicators-block {
      row-gap: 10px;
    }

    &.rest-key-indicators-block {
      row-gap: 12px;

      &.left {
        background-size: 64px;
        background-position: top -16px left -10px;
      }

      &.top-right {
        background-size: 64px;
        background-position: top -16px left -10px;
      }

      &.bottom-right {
        background-size: 64px;
        background-position: top -12px right -10px;
      }
    }
  }
`;

export const Text = styled.div`
  white-space: pre-line;

  &.report-title {
    font-size: 1.5rem;
    font-weight: 500;
    line-height: 3.25;
    letter-spacing: 1px;
    text-align: right;
    margin-right: -1px;

    @media (max-width: 768px) {
      text-align: center;
      font-size: 1.25rem;
      line-height: 3;
    }

    @media (max-width: 480px) {
      font-size: 1rem;
      line-height: 2.5;
    }
  }

  &.report-text {
    font-size: 5.25rem;
    line-height: 1.125;
    letter-spacing: 2px;
    padding-left: 80px;
    margin-right: -2px;

    @media (max-width: 768px) {
      font-size: 2rem;
      line-height: 1;
      text-align: center;
      padding-left: unset;
    }

    @media (max-width: 480px) {
      font-size: 1.5rem;
    }
  }

  &.about-text {
    font-size: 1rem;
    line-height: 1.75;
    letter-spacing: 0.75px;
    text-align: justify;

    &.title {
      font-size: 1.5rem;
      margin-bottom: 16px;
    }
  }

  .puppeteer-pdf &.about-text:not(.title) {
    display: -webkit-box;
    -webkit-line-clamp: 15;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &.score-title {
    font-size: 1.5rem;
    font-weight: 500;
    line-height: 3.25;
    letter-spacing: 1px;
    text-align: right;
    margin-right: -1px;

    @media (max-width: 768px) {
      text-align: center;
      font-size: 1.25rem;
      line-height: 3;
      padding-top: 48px;
    }

    @media (max-width: 480px) {
      font-size: 1rem;
      line-height: 2.5;
    }
  }

  &.sdg-number {
    font-size: 2.75rem;
    line-height: 1;
    font-weight: 500;
    letter-spacing: 1px;
    margin-right: -1px;

    @media (max-width: 768px) {
      font-size: 0.875rem;
    }

    @media (max-width: 480px) {
      font-size: 0.625rem;
    }
  }

  &.sdg-title {
    font-size: 2rem;
    line-height: 1;
    font-weight: 500;
    letter-spacing: 1px;
    padding-right: 16px;
    margin-right: -1px;

    @media (max-width: 768px) {
      font-size: 0.75rem;
    }

    @media (max-width: 480px) {
      font-size: 0.5rem;
    }
  }

  &.sdg-description {
    font-size: 1.25rem;
    font-weight: 500;
    letter-spacing: 0.625px;
    line-height: 1.875;

    @media (max-width: 768px) {
      font-size: 0.5rem;
    }

    @media (max-width: 480px) {
      font-size: 0.25rem;
    }
  }

  .puppeteer-pdf &.sdg-description {
    display: -webkit-box;
    -webkit-line-clamp: 8;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &.key-indicators-title {
    font-size: 1.5rem;
    font-weight: 500;
    line-height: 3.25;
    letter-spacing: 1px;
    text-align: right;
    margin-right: -1px;

    @media (max-width: 768px) {
      text-align: center;
      font-size: 1.25rem;
      line-height: 3;
    }

    @media (max-width: 480px) {
      font-size: 1rem;
      line-height: 2.5;
    }
  }

  &.radar-chart-title {
    font-size: 1.5rem;
    font-weight: 500;
    line-height: 3.25;
    letter-spacing: 1px;
    text-align: right;
    margin-right: -1px;

    @media (max-width: 768px) {
      text-align: center;
      font-size: 1.25rem;
      line-height: 3;
    }

    @media (max-width: 480px) {
      font-size: 1rem;
      line-height: 2.5;
    }
  }

  &.desc-title {
    font-size: 5rem;
    line-height: 1.125;

    @media (max-width: 768px) {
      font-size: 1.625rem;
      text-align: center;
    }

    @media (max-width: 480px) {
      font-size: 1.125rem;
    }
  }

  &.desc {
    font-size: 1rem;
    line-height: 1.75;

    &.number {
      width: 4.25rem;

      @media (max-width: 768px) {
        width: 3.5rem;
      }

      @media (max-width: 480px) {
        width: 3rem;
      }
    }

    &.title {
      margin-right: 8px;

      @media (max-width: 480px) {
        margin-right: 4px;
      }
    }

    &.subtitle {
      font-weight: 100;
    }

    @media (max-width: 768px) {
      font-size: 0.875rem;
      line-height: 1.6;
    }

    @media (max-width: 480px) {
      font-size: 0.75rem;
      line-height: 1.5;
    }
  }

  &.main-key-indicators-title {
    font-size: 6.125rem;
    line-height: 1.25;
    letter-spacing: 1.375px;
    margin-right: -1.375px;
    text-align: center;

    &.business-name {
      white-space: nowrap;
      width: fit-content;
      max-width: 100%;
      font-size: clamp(2rem, 6.125rem, 6.125rem);
      overflow: hidden;
      text-overflow: clip;

      @supports (container-type: inline-size) {
        font-size: clamp(1.5rem, 15cqi, 6.125rem);
      }
    }

    @media (max-width: 768px) {
      font-size: 2.5rem;
      line-height: 1.2;

      &.business-name {
        font-size: clamp(1.25rem, 10cqi, 2.5rem);
      }
    }

    @media (max-width: 480px) {
      font-size: 1.5rem;
      line-height: 1.15;

      &.business-name {
        font-size: clamp(1rem, 8cqi, 1.5rem);
      }
    }
  }

  &.main-key-indicators-desc {
    font-size: 1.375rem;
    font-weight: 300;
    line-height: 1.625;
    letter-spacing: 0.125px;
    margin-right: -0.125px;
    text-align: center;

    @media (max-width: 768px) {
      font-size: 0.875rem;
      line-height: 1.5;
      max-width: 60vw;
      margin: 0 auto;
    }

    @media (max-width: 480px) {
      font-size: 0.75rem;
      line-height: 1.4;
    }
  }

  .puppeteer-pdf &.main-key-indicators-desc {
    display: -webkit-box;
    -webkit-line-clamp: 6;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &.rest-key-indicators-number {
    font-size: 3.75rem;
    line-height: 1.125;
    letter-spacing: 1.25px;
    margin-right: -1.25px;

    @media (max-width: 768px) {
      font-size: 1.25rem;
      line-height: 1.1;
    }

    @media (max-width: 480px) {
      font-size: 0.75rem;
      line-height: 1.05;
    }
  }

  &.rest-key-indicators-title {
    font-size: 1.25rem;
    line-height: 1;
    letter-spacing: 0.75px;
    margin-right: -0.75px;

    @media (max-width: 768px) {
      font-size: 1.75rem;
    }

    @media (max-width: 480px) {
      font-size: 1.25rem;
    }
  }

  &.rest-key-indicators-desc {
    font-size: 0.875rem;
    line-height: 1.75;
    letter-spacing: 1px;
    margin-right: -1px;
    text-align: justify;

    @media (max-width: 768px) {
      font-size: 0.625rem;
      line-height: 1.6;
    }

    @media (max-width: 480px) {
      font-size: 0.5rem;
      line-height: 1.5;
    }
  }

  .puppeteer-pdf &.rest-key-indicators-desc {
    display: -webkit-box;
    -webkit-line-clamp: 5;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .puppeteer-pdf & {
    &.report-title {
      font-size: 0.75rem;
    }

    &.report-text {
      font-size: 2.625rem;
      padding-left: 40px;
    }

    &.about-text {
      font-size: 0.5rem;

      &.title {
        font-size: 0.75rem;
      }
    }

    &.score-title {
      font-size: 0.75rem;
    }

    &.sdg-number {
      font-size: 1.375rem;
    }

    &.sdg-title {
      font-size: 1rem;
      padding-right: 8px;
    }

    &.sdg-description {
      font-size: 0.625rem;
    }

    &.key-indicators-title {
      font-size: 0.75rem;
    }

    &.radar-chart-title {
      font-size: 0.75rem;
    }

    &.desc-title {
      font-size: 2.5rem;
      line-height: 1.125;
    }

    &.desc {
      font-size: 0.5rem;

      &.number {
        width: 2.125rem;
      }

      &.title {
        margin-right: 4px;
      }
    }

    &.main-key-indicators-title {
      font-size: 39px;

      &.business-name {
        white-space: nowrap;
        font-size: clamp(20px, 39px, 39px);
      }
    }

    &.main-key-indicators-desc {
      font-size: 8.5px;
    }

    &.rest-key-indicators-number {
      font-size: 1.875rem;
    }

    &.rest-key-indicators-title {
      font-size: 0.625rem;
    }

    &.rest-key-indicators-desc {
      font-size: 0.4375rem;
    }
  }
`;

export const Circle = styled.div<{
  $color: string;
  $bgColor: string;
}>`
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  color: ${({ $color }) => $color};
  background-color: ${({ $bgColor }) => $bgColor};

  &.center {
    width: 65.75vw;
    height: 65.75vw;
    top: -5.25%;
    left: 15.125%;
    z-index: 10;

    @media (max-width: 768px) {
      top: 3.75%;
      left: 50%;
      transform: translateX(-50%);
      width: 220vw;
      height: 220vw;
      align-items: start;
      padding-top: 48px;
      margin: 0 auto;
    }

    @media (max-width: 480px) {
    }
  }

  &.top-right {
    width: ${65.75 * 0.485}vw;
    height: ${65.75 * 0.485}vw;
    top: -4%;
    right: 3.75%;
    z-index: 11;

    @media (max-width: 768px) {
      border-radius: 20vh;
      top: 33.75%;
      right: 50%;
      transform: translateX(50%);
      width: 88vw;
      height: 20vh;
      margin: 0 auto;
    }

    @media (max-width: 480px) {
    }
  }

  &.left {
    width: ${65.75 * 0.475}vw;
    height: ${65.75 * 0.475}vw;
    top: 23.25%;
    left: -3.75%;
    z-index: 11;

    @media (max-width: 768px) {
      border-radius: 20vh;
      top: 55%;
      left: 50%;
      transform: translateX(-50%);
      width: 88vw;
      height: 20vh;
      margin: 0 auto;
    }

    @media (max-width: 480px) {
    }
  }

  &.bottom-right {
    width: ${65.75 * 0.435}vw;
    height: ${65.75 * 0.435}vw;
    top: 46.25%;
    right: -3.5%;
    z-index: 9;

    @media (max-width: 768px) {
      border-radius: 20vh;
      top: 76.25%;
      right: 50%;
      transform: translateX(50%);
      width: 88vw;
      height: 20vh;
      margin: 0 auto;
      z-index: 11;
    }

    @media (max-width: 480px) {
    }
  }
`;

export const FloatingExportButtons = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  z-index: 1000;

  button {
    padding: 12px 16px;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-weight: 500;
    font-size: 14px;
    color: white;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transition: all 0.3s ease;
    min-width: 200px;

    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.25);
    }

    &:active:not(:disabled) {
      transform: translateY(0);
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.6;
      transform: none;
    }

    &.puppeteer-pdf {
      background: linear-gradient(135deg, #2b5a87, #1e4666);

      &:hover:not(:disabled) {
        background: linear-gradient(135deg, #336699, #225577);
      }
    }
  }
`;

/* Global PDF Export Styles */
export const PDFStyles = css`
  /* Global box-sizing and overflow control */
  * {
    box-sizing: border-box;
  }

  #result-content {
    max-width: 100vw;
    overflow-x: hidden;
    width: 100%;
  }

  .puppeteer-pdf {
    .floating-export-buttons {
      display: none !important;
    }

    * {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      box-sizing: border-box !important;
    }

    /* Use system Chinese fonts for maximum compatibility */
    html,
    body {
      font-family:
        'Noto Sans TC',
        'Noto Sans CJK TC',
        -apple-system,
        BlinkMacSystemFont,
        'PingFang TC',
        'Hiragino Sans GB',
        'Microsoft JhengHei UI',
        'Microsoft JhengHei',
        'Segoe UI',
        'Source Han Sans TC',
        'Helvetica Neue',
        Arial,
        sans-serif !important;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
      margin: 0;
      padding: 0;
      width: 100%;
      height: 100%;
    }

    * {
      font-family: inherit !important;
    }

    #root {
      margin: 0;
      padding: 0;
      width: 100%;
      max-width: 100% !important;
      overflow-x: hidden !important;
    }
  }
`;
